import { useState, useEffect } from 'react';
import ThemeToggle from './ThemeToggle';
import { useNavigate } from 'react-router-dom';

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [login, setLogin] = useState({ username: '', password: '' });
  const [mfaCode, setMfaCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [content, setContent] = useState([]);
  const [status, setStatus] = useState('');
  const [newBlock, setNewBlock] = useState({ section: '', key: '', text: '', url: '', active: true });
  const [availableKeys, setAvailableKeys] = useState([]);
  const [customKey, setCustomKey] = useState('');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const isLoggedIn = Boolean(token);
const [mfaPrompted, setMfaPrompted] = useState(false);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    if (token) fetchContent();
  }, [token]);

  const fetchContent = async () => {
    try {
      const res = await fetch(`https://adrianrudge.co.uk/api/admin/content`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      setContent(data);
    } catch (err) {
      setStatus('Failed to load content');
    }
  };

const handleLogin = async () => {
  setStatus('Logging in...');
  try {
    const payload = {
      username: login.username,
      password: login.password,
    };
    if (mfaCode.trim()) {
      payload.token = mfaCode.trim();
    }

    const res = await fetch(`https://adrianrudge.co.uk/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const text = await res.text();
    console.log('[DEBUG] Raw login response:', text);

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`Server response was not valid JSON:\n${text}`);
    }

    if (res.ok && data.token) {
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setStatus('');
    } else if (res.status === 403 && data.mfaRequired) {
      setStatus('MFA code required');
      setMfaPrompted(true); // Show MFA input only after server requires it
    } else {
      setStatus(data.error || 'Login failed');
    }
  } catch (err) {
    setStatus('Login error: ' + err.message);
  }
};


  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken('');
    navigate('/');
  };

  const handleStartMfaSetup = async () => {
    try {
      const res = await fetch(`https://adrianrudge.co.uk/api/admin/setup-mfa`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      setQrCode(data.qrCode);
      setShowMfaSetup(true);
    } catch (err) {
      setStatus('Failed to setup MFA');
    }
  };

  const handleVerifyMfa = async () => {
    try {
      const res = await fetch(`https://adrianrudge.co.uk/api/admin/verify-mfa-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ token: mfaCode }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Server response was not valid JSON:\n${text}`);
      }

      if (res.ok) {
        alert('MFA setup complete!');
        setShowMfaSetup(false);
        setQrCode('');
        setMfaCode('');
      } else {
        setStatus(data.error || 'Verification failed');
      }
    } catch (err) {
      setStatus('Error: ' + err.message);
    }
  };

  const handleUpdate = async (item) => {
    await fetch(`https://adrianrudge.co.uk/api/admin/content/${item.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(item),
    });
    fetchContent();
  };

  const handleCreate = async () => {
    const block = {
      ...newBlock,
      key: newBlock.key === '__custom' ? customKey : newBlock.key,
    };
    const res = await fetch(`https://adrianrudge.co.uk/api/admin/content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(block),
    });
    if (res.ok) {
      setNewBlock({ section: '', key: '', text: '', url: '', active: true });
      setCustomKey('');
      fetchContent();
    } else {
      setStatus('Failed to create block.');
    }
  };

  const handlePasswordChange = async () => {
    try {
      const res = await fetch(`https://adrianrudge.co.uk/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(passwordForm),
      });
      const data = await res.json();
      setPasswordMessage(data.message || data.error);
    } catch (err) {
      setPasswordMessage('Failed to update password.');
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="p-4 font-mono min-h-screen bg-white dark:bg-black text-black dark:text-green-400">
        <h2 className="text-xl mb-4">Admin Login</h2>
        <input
          className="block mb-2 p-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400"
          placeholder="Username"
          value={login.username}
          onChange={(e) => setLogin({ ...login, username: e.target.value })}
        />
        <input
          type="password"
          className="block mb-2 p-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400"
          placeholder="Password"
          value={login.password}
          onChange={(e) => setLogin({ ...login, password: e.target.value })}
        />
        <input
          type="text"
          placeholder="MFA Code (if enabled)"
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value)}
          className="block mb-2 p-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400"
        />
        <button
          className="border px-4 py-2 border-green-400 hover:bg-green-400 hover:text-black"
          onClick={handleLogin}
        >
          Login
        </button>
        <p className="mt-2">{status}</p>
      </div>
    );
  }

  return (
    <div className="p-4 font-mono min-h-screen bg-white dark:bg-black text-black dark:text-green-400">
      <div className="flex justify-between items-center mb-4 pr-36">
        <h2 className="text-xl">Admin Dashboard</h2>
        <button
          onClick={handleLogout}
          className="ml-4 border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
        >
          Logout
        </button>
      </div>

      <div className="absolute top-4 right-4">
        <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />
      </div>

      <button
        onClick={handleStartMfaSetup}
        className="mb-4 border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
      >
        Setup MFA
      </button>

      {showMfaSetup && qrCode && (
        <div className="mb-6 border border-green-400 p-4">
          <h3 className="font-bold mb-2">Scan this QR Code with your Authenticator App</h3>
          <img src={qrCode} alt="Scan with Authenticator app" className="mb-2" />
        </div>
      )}

      {showMfaSetup && (
        <div className="mt-4">
          <input
            type="text"
            placeholder="Enter code from Authenticator"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            className="w-full mb-2 p-2 border border-green-400 text-black dark:text-green-400 bg-white dark:bg-black"
          />
          <button
            onClick={handleVerifyMfa}
            className="border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
          >
            Verify MFA
          </button>
        </div>
      )}

      <button
        onClick={() => setShowPasswordForm(!showPasswordForm)}
        className="mb-4 border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
      >
        {showPasswordForm ? 'Hide Password Form' : 'Change Password'}
      </button>

      {showPasswordForm && (
        <div className="mb-6 border border-green-400 p-4">
          <h3 className="font-bold mb-2">Change Password</h3>
          <input
            type="password"
            placeholder="Current Password"
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            className="w-full mb-2 p-2 border border-green-400 bg-white dark:bg-black text-black dark:text-green-400"
          />
          <input
            type="password"
            placeholder="New Password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            className="w-full mb-2 p-2 border border-green-400 bg-white dark:bg-black text-black dark:text-green-400"
          />
          <button
            onClick={handlePasswordChange}
            className="border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
          >
            Update Password
          </button>
          {passwordMessage && <p className="mt-2">{passwordMessage}</p>}
        </div>
      )}

      {/* New Block Form */}
      <div className="mb-6 border border-green-400 p-4">
        <h3 className="font-bold mb-2">New Content Block</h3>
        <select
          value={newBlock.section}
          onChange={(e) => {
            const section = e.target.value;
            setNewBlock((prev) => ({ ...prev, section }));
            const keys = content
              .filter((item) => item.section === section)
              .map((item) => item.key);
            setAvailableKeys([...new Set(keys)]);
          }}
          className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
        >
          <option value="">-- Select Section --</option>
          <option value="about">About</option>
          <option value="portfolio">Portfolio</option>
          <option value="resume">Resume</option>
          <option value="contact">Contact</option>
        </select>

        <select
          value={newBlock.key}
          onChange={(e) => setNewBlock((prev) => ({ ...prev, key: e.target.value }))}
          className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
        >
          <option value="">-- Select Key --</option>
          {availableKeys.map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
          <option value="__custom">+ Custom Key</option>
        </select>

        {newBlock.key === '__custom' && (
          <input
            placeholder="Custom Key"
            value={customKey}
            onChange={(e) => {
              setCustomKey(e.target.value);
              setNewBlock((prev) => ({ ...prev, key: e.target.value }));
            }}
            className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
          />
        )}

        <textarea
          placeholder="Text"
          value={newBlock.text}
          onChange={(e) => setNewBlock({ ...newBlock, text: e.target.value })}
          className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
        />
        <input
          placeholder="URL (optional)"
          value={newBlock.url}
          onChange={(e) => setNewBlock({ ...newBlock, url: e.target.value })}
          className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
        />
        <label className="block mb-2">
          <input
            type="checkbox"
            checked={newBlock.active}
            onChange={(e) => setNewBlock({ ...newBlock, active: e.target.checked })}
          />{' '}
          Active
        </label>
        <button
          onClick={handleCreate}
          className="border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
        >
          Create Block
        </button>
      </div>

      {/* Existing Content Blocks */}
      {content.map((item) => (
        <div key={item.id} className="mb-6 border border-green-400 p-4">
          <h3 className="font-bold mb-1">{item.section} / {item.key}</h3>
          <textarea
            value={item.text}
            onChange={(e) => (item.text = e.target.value)}
            className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
          />
          <input
            value={item.url || ''}
            placeholder="URL (optional)"
            onChange={(e) => (item.url = e.target.value)}
            className="w-full mb-2 bg-white dark:bg-black border border-green-400 text-black dark:text-green-400 p-2"
          />
          <div className="flex items-center gap-4 mt-2">
            <label>
              <input
                type="checkbox"
                checked={item.active}
                onChange={(e) => (item.active = e.target.checked ? 1 : 0)}
              />{' '}
              Active
            </label>
            <button
              onClick={() => handleUpdate(item)}
              className="border px-3 py-1 border-green-400 hover:bg-green-400 hover:text-black"
            >
              Save Changes
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
